import React, { useRef, useMemo } from "react";
import { motion } from "framer-motion";
import DottedMap from "dotted-map";


export default function WorldMap({
  dots = [],
  lineColor = "#0ea5e9",
  theme = "dark",
  width = 800,
  height = 400,
}) {
  const svgRef = useRef(null);

  // create DottedMap instance once
  const map = useMemo(
    () => new DottedMap({ height: 100, grid: "diagonal" }),
    []
  );


  const svgMap = useMemo(
    () =>
      map.getSVG({
        radius: 0.22,
        // color: theme === "dark" ? "#FFFFFF40" : "#00000040",
        color: "#FFCD72",
        shape: "circle",
        // backgroundColor: "white",
      }),
    [map]
  );

  // simple equirectangular projection: lng -> x, lat -> y
  const projectPoint = (lat, lng) => {
    // note: width and height must match the SVG viewBox used below
    const x = (lng + 180) * (width / 360);
    const y = (90 - lat) * (height / 180);
    return { x, y };
  };

  // create a quadratic Bezier path string between start & end
  const createCurvedPath = (start, end) => {
    const midX = (start.x + end.x) / 2;
    const midY = Math.min(start.y, end.y) - 50; // push curve upward a bit
    return `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${end.y}`;
  };

  return (
    <div className="w-full aspect-[2/1] relative font-sans">
      {/* --- Background: inject the dotted map SVG safely (avoids JSX parsing trouble) --- */}
      <div
        className="absolute inset-0 pointer-events-none select-none"
        aria-hidden="true"
        // inject raw svg string safely
        dangerouslySetInnerHTML={{ __html: svgMap }}
      />

      {/* --- Overlay SVG for paths and labels --- */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-full absolute inset-0"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Gradient used for animated path */}
        <defs>
          <linearGradient id="path-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="5%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="95%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Animated curved paths */}
        {dots.map((dot, i) => {
          const startPoint = projectPoint(dot.start.lat, dot.start.lng);
          const endPoint = projectPoint(dot.end.lat, dot.end.lng);
          const d = createCurvedPath(startPoint, endPoint);
          return (
            <motion.path
              key={`path-${i}`}
              d={d}
              fill="none"
              stroke="url(#path-gradient)"
              strokeWidth={1.2}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.1, delay: 0.4 * i, ease: "easeOut" }}
            />
          );
        })}

        {/* Markers + labels */}
        {dots.map((dot, i) => {
          const s = projectPoint(dot.start.lat, dot.start.lng);
          const e = projectPoint(dot.end.lat, dot.end.lng);

          // small helper to render text with a subtle backdrop for readability
          const makeLabel = (x, y, text, key, anchor = "start") => (
            <g key={key}>
              {/* subtle backdrop using a tiny rect (improves readability on dotted bg) */}
              <rect
                x={x + (anchor === "end" ? -6 - text.length * 6 : 6)}
                y={y - 10}
                rx="3"
                ry="3"
                width={Math.max(28, text.length * 7 + 18)}
                height="16"
                fill={
                  theme === "dark" ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.6)"
                }
                stroke="none"
              />
              <text
                x={x + 10}
                y={y + 3}
                fontSize="10"
                fill={theme === "dark" ? "#fff" : "#000"}
                style={{ fontFamily: "inherit" }}
              >
                {text}
              </text>
            </g>
          );

          return (
            <g key={`point-group-${i}`}>
              {/* Start marker */}
              <g>
                <circle cx={s.x} cy={s.y} r="3" fill={lineColor} />
                {dot.start &&
                  dot.start.label &&
                  makeLabel(s.x, s.y, `📍 ${dot.start.label}`, `s-label-${i}`)}
              </g>

              {/* End marker */}
              <g>
                <circle cx={e.x} cy={e.y} r="3" fill={lineColor} />
                {dot.end &&
                  dot.end.label &&
                  makeLabel(e.x, e.y, `📍 ${dot.end.label}`, `e-label-${i}`)}
              </g>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
